import { FC } from "react";
import { TableHead } from "@/components/Base/Table";
import { cn } from "@/lib/utils";
import { SortIndicator } from "./SortIndicator";
import { ColumnConfig, SortCriteria } from "../types";
import { getColumnDisplayLabel } from "../hooks/useEnhancedColumns";

export interface SortableTableHeaderProps {
  column: ColumnConfig;
  sortCriteria: SortCriteria[];
  onSort: (field: string, ctrlKey: boolean) => void;
}

export const SortableTableHeader: FC<SortableTableHeaderProps> = ({
  column,
  sortCriteria,
  onSort,
}) => {
  const currentSort = sortCriteria.find((sort) => sort.field === column.field);
  const isActive = Boolean(currentSort);
  const displayLabel = getColumnDisplayLabel(column);

  const handleClick = (event: React.MouseEvent) => {
    if (column.sortable) {
      onSort(column.field, event.ctrlKey || event.metaKey);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (column.sortable && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      onSort(column.field, event.ctrlKey || event.metaKey);
    }
  };

  return (
    <TableHead
      style={{ width: column.width, minWidth: column.minWidth }}
      className={cn(
        "select-none p-1 text-sm transition-colors",
        column.sortable
          ? "cursor-pointer hover:bg-muted/50 focus:bg-muted/50 focus:outline-none"
          : "cursor-default",
        column.className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={column.sortable ? 0 : -1}
      role={column.sortable ? "button" : undefined}
      aria-sort={
        isActive
          ? currentSort?.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
      aria-label={
        column.sortable
          ? `Sort by ${displayLabel}${
              isActive
                ? `, currently sorted ${
                    currentSort?.direction === "asc"
                      ? "ascending"
                      : "descending"
                  }${
                    currentSort?.priority !== undefined &&
                    currentSort.priority > 0
                      ? ` (priority ${currentSort.priority + 1})`
                      : ""
                  }`
                : ""
            }`
          : displayLabel
      }
    >
      <div className="flex items-center">
        <span className={cn("text-white", `text-${column.align}`)}>
          {displayLabel}
        </span>
        {column.sortable && (
          <SortIndicator
            direction={currentSort?.direction}
            priority={currentSort?.priority}
            isActive={isActive}
          />
        )}
      </div>
    </TableHead>
  );
};
